import { useEffect, useState } from "react";
import { Eye, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { api, errMsg, fileUrl } from "@/lib/api";
import { PAGE, cropBox, cropStyle, frameMap, pct } from "@/lib/layout";

const toBespoke = (d) => {
  const sx = 1380 / 1278, sy = 685 / 464;
  return { ...d, x: 30 + (d.x - 81) * sx, y: 100 + (d.y - 169) * sy, w: d.w * sx, h: d.h * sy };
};

const BespokeChrome = ({ p, brand }) => (
  <>
    <div className="absolute flex items-center" style={{ left: pct(30, PAGE.w), top: pct(24, PAGE.h), height: pct(52, PAGE.h), maxWidth: pct(320, PAGE.w) }}>
      {brand?.logo ? <img src={fileUrl(brand.logo, 400)} alt="logo" className="h-full w-auto object-contain" />
        : <span className="border border-[var(--ink)] px-[0.8cqw] py-[0.2cqw] wordmark" style={{ fontSize: "1.05cqw" }}>{brand?.brand_name || "BRAND"}</span>}
    </div>
    <span className="absolute text-[#6B6B6B]" style={{ right: pct(30, PAGE.w), top: pct(40, PAGE.h), fontSize: "1.05cqw" }}>{p.code || "CODE"} | {p.name || "Product name"}</span>
  </>
);

const PageMock = ({ p, cat, frames: raw, active, onPick, brand }) => {
  const byPath = Object.fromEntries(p.images.map((i) => [i.path, i]));
  const bespoke = cat.style === "bespoke";
  const frames = bespoke ? raw.map(toBespoke) : raw;
  return (
    <div className={`relative w-full aspect-[16/9] ${bespoke ? "bg-white" : "bg-[var(--cream)]"} page-shadow overflow-hidden`} style={{ containerType: "inline-size" }} data-testid="page-mock">
      {bespoke ? <BespokeChrome p={p} brand={brand} /> : <>
        <span className="absolute wordmark" style={{ left: pct(81, PAGE.w), top: pct(76, PAGE.h), fontSize: "1.55cqw" }}>{brand?.brand_name || "JORDYBLUE"}</span>
        <span className="absolute" style={{ right: pct(81, PAGE.w), top: pct(79, PAGE.h), fontSize: "1.05cqw" }}>{cat.catalog_title}&nbsp;&nbsp;&nbsp;&nbsp;{cat.edition}</span>
      </>}
      {frames.map((d) => {
        const k = d.key;
        const s = p.slots.find((x) => x.slot === k);
        const ref = s && byPath[s.image];
        return (
          <button key={k} onClick={() => onPick(k)} data-testid={`slot-${k}`}
            className={`absolute overflow-hidden bg-[var(--line)] transition-shadow ${active === k ? "ring-2 ring-[var(--terracotta)] ring-offset-2 z-10" : "hover:ring-1 hover:ring-[var(--ink)]"}`}
            style={{ left: pct(d.x, PAGE.w), top: pct(d.y, PAGE.h), width: pct(d.w, PAGE.w), height: pct(d.h, PAGE.h) }}>
            {ref && <img src={fileUrl(ref.path, 1000)} alt="" style={cropStyle(ref, d, s)} draggable={false} />}
          </button>
        );
      })}
      {!bespoke && <>
        <div className="absolute inset-x-0 border-t border-[#A3A09A]" style={{ top: pct(680, PAGE.h) }} />
        <div className="absolute inset-x-0 border-t border-[#A3A09A]" style={{ top: pct(739, PAGE.h) }} />
        <span className="absolute font-bold" style={{ left: pct(81, PAGE.w), top: pct(696, PAGE.h), fontSize: "1.25cqw" }}>{p.code || `${cat.code_prefix}…`}</span>
        <span className="absolute font-bold uppercase" style={{ right: pct(81, PAGE.w), top: pct(695, PAGE.h), fontSize: "1.45cqw" }}>{p.name || "Product name"}</span>
      </>}
    </div>
  );
};

const FocalPicker = ({ ref_, d, s, onChange }) => {
  const [x0, y0, bw, bh] = cropBox(ref_.width, ref_.height, d.w, d.h, s.cx, s.cy, s.zoom);
  const click = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    onChange({ cx: (e.clientX - r.left) / r.width, cy: (e.clientY - r.top) / r.height });
  };
  return (
    <div className="relative overflow-hidden cursor-crosshair select-none" onClick={click} data-testid="focal-picker">
      <img key={ref_.path} src={fileUrl(ref_.path, 800)} alt="" className="w-full block" draggable={false} />
      <div className="absolute border-2 border-[var(--terracotta)] shadow-[0_0_0_9999px_rgba(37,37,35,0.45)] pointer-events-none transition-all duration-150"
        style={{ left: pct(x0, ref_.width), top: pct(y0, ref_.height), width: pct(bw, ref_.width), height: pct(bh, ref_.height) }} />
    </div>
  );
};

const SlotPanel = ({ p, k, frames, setSlot }) => {
  const s = p.slots.find((x) => x.slot === k);
  const d = frameMap(frames)[k];
  if (!s || !d) return <p className="text-sm text-[var(--muted-ink)]">Click a frame on the page to adjust it.</p>;
  const ref = p.images.find((i) => i.path === s.image) || p.images[0];
  return (
    <div className="space-y-5" data-testid="slot-panel">
      <div>
        <p className="text-[11px] tracking-[0.25em] uppercase text-[var(--muted-ink)]">Editing frame</p>
        <h3 className="text-lg font-bold">{d.label}</h3>
        {s.why && <p className="text-xs text-[var(--muted-ink)] mt-1 italic" data-testid="slot-why">AI: {s.why}</p>}
      </div>
      <div className="grid grid-cols-5 gap-1.5">
        {p.images.map((img, i) => (
          <button key={img.path} onClick={() => setSlot(k, { image: img.path, cx: 0.5, cy: 0.5, zoom: 1 })} data-testid={`slot-image-option-${i}`}
            className={`aspect-square overflow-hidden border-2 ${img.path === s.image ? "border-[var(--terracotta)]" : "border-transparent opacity-70 hover:opacity-100"}`}>
            <img src={fileUrl(img.path, 200)} alt="" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
      {ref && <FocalPicker ref_={ref} d={d} s={s} onChange={(v) => setSlot(k, v)} />}
      <p className="text-xs text-[var(--muted-ink)]">Click the photo to move the crop. Zoom in to split one photo into a detail shot.</p>
      <div>
        <div className="flex justify-between text-[11px] tracking-[0.2em] uppercase mb-3"><span>Zoom</span><span data-testid="slot-zoom-value">{s.zoom.toFixed(2)}×</span></div>
        <Slider min={1} max={4} step={0.05} value={[s.zoom]} onValueChange={([z]) => setSlot(k, { zoom: z })} data-testid="slot-zoom-slider" />
      </div>
    </div>
  );
};

export const LayoutStep = ({ p, update, cat, frames, layoutName }) => {
  const [active, setActive] = useState(frames[0]?.key);
  const [busy, setBusy] = useState(false);
  const [tips, setTips] = useState("");
  const [preview, setPreview] = useState(null);
  const [pv, setPv] = useState(false);
  const [brand, setBrand] = useState(null);
  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      const name = data.brands.some((b) => b.name === cat.brand_name) ? cat.brand_name : data.brand_name;
      const prof = data.brands.find((b) => b.name === name);
      setBrand({ ...(prof || {}), brand_name: name });
    });
  }, [cat.brand_name]);

  const setSlot = (k, patch) => update({ slots: p.slots.map((s) => (s.slot === k ? { ...s, ...patch, why: patch.image ? "" : s.why } : s)) });
  const suggest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/layout", { images: p.images, name: p.name, highlights: p.highlights, category_id: cat.id });
      update({ slots: [...data.slots, ...p.slots.filter((s) => s.slot === "sheet")] }); setTips(data.tips); toast.success("AI arranged the image page");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const exact = async () => {
    setPv(true);
    try { const { data } = await api.post("/preview", p); setPreview(data.pages); } catch (e) { toast.error(errMsg(e)); }
    setPv(false);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Button onClick={suggest} disabled={busy} className="bg-[var(--ink)]" data-testid="ai-layout-button">
          {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Sparkles size={14} className="mr-2" />}Suggest layout with AI</Button>
        <Button variant="outline" onClick={exact} disabled={pv} data-testid="exact-preview-button">
          {pv ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Eye size={14} className="mr-2" />}Render exact pages</Button>
        <p className="text-xs border border-[var(--line)] px-2 py-1" data-testid="layout-name">Layout: {layoutName}</p>
        <p className="text-xs text-[var(--muted-ink)]">{p.highlights.length ? `Using highlights: ${p.highlights.join(", ")}` : "Tip: add key highlights in Details for smarter picks"}</p>
      </div>
      {tips && <p className="text-sm bg-[var(--cream)] border-l-2 border-[var(--terracotta)] px-4 py-3 mb-6" data-testid="ai-tips">{tips}</p>}
      <div className="grid xl:grid-cols-[1fr_360px] gap-10">
        <div>
          <PageMock p={p} cat={cat} frames={frames.filter((f) => !f.sheet)} active={active} onPick={setActive} brand={brand} />
          {frames.some((f) => f.sheet) && (
            <button onClick={() => setActive("sheet")} data-testid="slot-sheet"
              className={`mt-4 flex items-center gap-3 border-2 p-2 pr-4 text-sm transition-colors ${active === "sheet" ? "border-[var(--terracotta)]" : "border-[var(--line)]"}`}>
              {(() => { const s = p.slots.find((x) => x.slot === "sheet"); return s?.image ? <img src={fileUrl(s.image, 200)} alt="" className="w-16 h-16 object-cover" /> : <span className="w-16 h-16 bg-[var(--line)]" />; })()}
              <span className="text-left"><span className="block font-semibold">Data sheet photo</span><span className="text-xs text-[var(--muted-ink)]">Half-page photo next to the price table</span></span>
            </button>
          )}
        </div>
        <div className="bg-[var(--cream)] p-6 border border-[var(--line)]"><SlotPanel p={p} k={active} frames={frames} setSlot={setSlot} /></div>
      </div>
      {preview && (
        <div className="mt-12" data-testid="exact-preview">
          <h2 className="text-base md:text-lg font-bold mb-4">Exact catalog pages</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {preview.map((b, i) => <img key={i} src={`data:image/png;base64,${b}`} alt={`page ${i + 1}`} className="w-full page-shadow" data-testid={`exact-preview-page-${i}`} />)}
          </div>
        </div>
      )}
    </div>
  );
};
